import {
    AfterContentInit,
    ChangeDetectorRef,
    ContentChildren,
    Directive,
    EventEmitter,
    forwardRef,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { defer, merge, Observable, Subject, Subscription } from 'rxjs';
import {
    RadioButtonComponent,
    RadioChangeEvent,
} from './radio-button.component';
import { switchMap, take, takeUntil } from 'rxjs/operators';

/* eslint-disable @angular-eslint/no-forward-ref */

@Directive({
    selector: '[ihdRadioGroup]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => RadioGroupDirective),
            multi: true,
        },
    ],
})
export class RadioGroupDirective
    implements OnInit, AfterContentInit, OnDestroy, ControlValueAccessor {
    /**
     * Value for the radio-group. Should equal the value of the selected radio button if there is
     * a corresponding radio button with a matching value. If there is not such a corresponding
     * radio button, this value persists to be applied in case a new radio button is added with a
     * matching value.
     */
    @Input()
    set value(value: any) {
        if (this._value !== value) {
            // Set this before proceeding to ensure no circular loop occurs with selection.
            this._value = value;

            this.updateSelectedRadioFromValue();
            this.checkSelectedRadioButton();
        }
    }

    get value(): any {
        return this._value;
    }

    /**
     * The currently selected radio button. If set to a new radio button, the radio group value
     * will be updated to match the new selected button.
     */
    @Input()
    get selected() {
        return this._selected;
    }

    // type of selected is RadioButtonComponent|null but getting forward reference error so setting to null
    set selected(selected: any | null) {
        this._selected = selected;
        this.value = selected ? selected.value : null;
        this.checkSelectedRadioButton();
    }

    @Output()
    change: EventEmitter<RadioChangeEvent> = new EventEmitter<RadioChangeEvent>();

    public onChange: any;
    public onTouched: any;

    readonly radioSelectChanges: Observable<RadioChangeEvent> = defer(() => {
        if (this.radios) {
            return merge(
                ...this.radios.map(
                    (radio: RadioButtonComponent) => radio.change
                )
            );
        }

        // If there are any subscribers before `ngAfterViewInit`, the `radios` will be undefined.
        // Return a stream that we'll replace with the real one once everything is in place.
        return this.ngZone.onStable.asObservable().pipe(
            take(1),
            switchMap(() => this.radios)
        );
    }) as Observable<RadioChangeEvent>;

    @ContentChildren(forwardRef(() => RadioButtonComponent), {
        descendants: true,
    })
    radios: QueryList<RadioButtonComponent>;

    private destroyed$: Subject<void> = new Subject();
    private _value: any;
    private _selected: RadioButtonComponent | null = null;
    private radioSelectSubscription: Subscription;
    private isInitialized: boolean;

    constructor(private ngZone: NgZone, private cdr: ChangeDetectorRef) {}

    ngOnInit() {}

    ngAfterContentInit() {
        // Mark this component as initialized in AfterContentInit because the initial value can
        // possibly be set by NgModel on MatRadioGroup, and it is possible that the OnInit of the
        // NgModel occurs *after* the OnInit of the MatRadioGroup.
        this.isInitialized = true;

        // this.updateSelectedRadioFromValue();
        //
        merge(
            this.radios.changes,
            this.ngZone.onStable.asObservable().pipe(take(1))
        )
            .pipe(takeUntil(this.destroyed$))
            .subscribe((changes) => {
                if (this.radioSelectSubscription) {
                    this.radioSelectSubscription.unsubscribe();
                }

                this.radioSelectSubscription = this.radioSelectChanges
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe((selectEvent: RadioChangeEvent) => {
                        this.radios.forEach((radio: RadioButtonComponent) => {
                            if (radio !== selectEvent.source) {
                                radio.checked = false;
                            }
                        });
                    });
            });
    }

    ngOnDestroy() {
        this.destroyed$.next();
    }

    writeValue(value: any) {
        this.value = value;
    }

    /**
     * Set the function to be called
     * when the control receives a change event.
     */
    registerOnChange(fn: (_: any) => {}) {
        this.onChange = fn;
    }

    /**
     * Set the function to be called
     * when the control receives a touch event.
     */
    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    emitChangeEvent(): void {
        if (this.isInitialized) {
            this.change.emit(
                new RadioChangeEvent(
                    this.selected!,
                    this.value,
                    this.selected?.checked
                )
            );
            if (this.onChange) {
                this.onChange(this.value);
            }
        }
    }

    private checkSelectedRadioButton() {
        if (this._selected && !this._selected.checked) {
            this._selected.checked = true;
        }
    }

    private updateSelectedRadioFromValue(): void {
        // If the value already matches the selected radio, do nothing.
        const isAlreadySelected =
            this.selected !== null && this.selected.value === this.value;

        if (this.radios && !isAlreadySelected) {
            this.selected = null;
            this.radios.forEach((radio: RadioButtonComponent) => {
                radio.checked = this.value === radio.value;
                if (radio.checked) {
                    this.selected = radio;
                }
            });
        }
    }
}
